import React, { createContext, useContext, useState, useCallback } from 'react';

interface TabConfig {
  id: string;
  label: string;
}

interface TabContextState {
  tabs: Map<string, TabConfig[]>;
  activeTab: Map<string, string>;
  setTabsConfig: (tabs: TabConfig[], activeTab: string, onChange?: (tabId: string) => void) => string;
  clearTabs: (tabsId: string) => void;
  setActiveTab: (tabsId: string, tabId: string) => void;
  getActiveTabs: (tabsId: string) => TabConfig[] | undefined;
  getActiveTabId: (tabsId: string) => string | undefined;
}

const TabContext = createContext<TabContextState | undefined>(undefined);

export const TabProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tabs, setTabs] = useState<Map<string, TabConfig[]>>(new Map());
  const [activeTab, setActiveTabState] = useState<Map<string, string>>(new Map());
  const [callbacks, setCallbacks] = useState<Map<string, (tabId: string) => void>>(new Map());

  const setTabsConfig = useCallback(
    (newTabs: TabConfig[], initialActive: string, onChange?: (tabId: string) => void) => {
      const tabsId = `tabs-${Date.now()}-${Math.random()}`;
      
      setTabs(prev => new Map(prev).set(tabsId, newTabs));
      setActiveTabState(prev => new Map(prev).set(tabsId, initialActive));
      
      if (onChange) {
        setCallbacks(prev => new Map(prev).set(tabsId, onChange));
      }

      return tabsId;
    },
    []
  );

  const clearTabs = useCallback((tabsId: string) => {
    setTabs(prev => {
      const newMap = new Map(prev);
      newMap.delete(tabsId);
      return newMap;
    });
    
    setActiveTabState(prev => {
      const newMap = new Map(prev);
      newMap.delete(tabsId);
      return newMap;
    });

    setCallbacks(prev => {
      const newMap = new Map(prev);
      newMap.delete(tabsId);
      return newMap;
    });
  }, []);

  const setActiveTab = useCallback((tabsId: string, tabId: string) => {
    setActiveTabState(prev => new Map(prev).set(tabsId, tabId));
    
    const callback = callbacks.get(tabsId);
    if (callback) {
      callback(tabId);
    }
  }, [callbacks]);

  const getActiveTabs = useCallback((tabsId: string) => {
    return tabs.get(tabsId);
  }, [tabs]);

  const getActiveTabId = useCallback((tabsId: string) => {
    return activeTab.get(tabsId);
  }, [activeTab]);

  return (
    <TabContext.Provider
      value={{
        tabs,
        activeTab,
        setTabsConfig,
        clearTabs,
        setActiveTab,
        getActiveTabs,
        getActiveTabId,
      }}
    >
      {children}
    </TabContext.Provider>
  );
};

export const useTabs = () => {
  const context = useContext(TabContext);
  if (!context) {
    throw new Error('useTabs must be used within a TabProvider');
  }
  return context;
};
